/**
 * Read-side for the go-live checklist on the apartment detail (COL-529).
 *
 * The wizard saved everything into apartment_checklist_items / _files, but no
 * detail surface read it back, so a freshly-created property looked empty of the
 * photos and the Hausverwaltung/caretaker contacts the owner had entered. This
 * card shows what's stored: the x/15 status grid, the contacts parsed back out
 * of the notes, and the uploaded proof photos (opened via a signed URL — the
 * `contracts` bucket is private).
 *
 * Status is read straight from each stored row (fulfilled/not_applicable → done,
 * requested → requested, else open) rather than recomputed, so it can't drift
 * from what the DB holds. rowsToChecklist is used only to un-fold the contacts.
 */
import { Check, CircleAlert, Clock, FileText, Image } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { openStoredFile } from "@/lib/storage-url";
import { CONTRACTS_BUCKET } from "@/lib/v2-upload";
import {
  CHECKLIST_ITEM_LABELS,
  rowsToChecklist,
  type Person,
} from "@/components/admin/v2/wizard/checklist";
import { useApartmentChecklist } from "@/hooks/v2/useApartmentChecklist";

/** Stored status → the three display states the grid renders. */
export function reviewStatus(stored: string): "done" | "requested" | "open" {
  if (stored === "fulfilled" || stored === "not_applicable") return "done";
  if (stored === "requested") return "requested";
  return "open";
}

export function ApartmentChecklistCard({
  apartmentId,
  bedrooms,
}: {
  apartmentId: number;
  /** Drives nothing in the display here; passed to rowsToChecklist for parity. */
  bedrooms: number;
}) {
  const { t } = useLanguage();
  const { data, isLoading } = useApartmentChecklist(apartmentId);

  if (isLoading) {
    return (
      <Card className="p-5">
        <Skeleton className="h-6 w-48 mb-3" />
        <Skeleton className="h-24 w-full" />
      </Card>
    );
  }

  const items = data?.items ?? [];
  const files = data?.files ?? [];

  if (items.length === 0) {
    return (
      <Card className="p-5">
        <Header t={t} done={0} total={15} />
        <p className="mt-3 text-sm text-muted-foreground">
          {t(
            "admin.v2.apartmentDetail.checklist.empty",
            "No onboarding checklist was filled for this apartment yet."
          )}
        </p>
      </Card>
    );
  }

  const byKey = new Map(items.map((r) => [r.item_key, r]));
  const doneCount = items.filter(
    (r) => reviewStatus(r.status) === "done"
  ).length;

  // Contacts are folded into the notes; un-fold them back into people.
  const checklist = rowsToChecklist(items, { bedrooms });
  const contacts: { label: string; person: Person }[] = [
    {
      label: t(
        "admin.v2.apartmentDetail.checklist.propertyManager",
        "Property manager"
      ),
      person: checklist.people.hv,
    },
    {
      label: t("admin.v2.apartmentDetail.checklist.caretaker", "Caretaker"),
      person: checklist.people.hm,
    },
    {
      label: t("admin.v2.apartmentDetail.checklist.keyContact", "Key handover"),
      person: checklist.people.key,
    },
    {
      label: t(
        "admin.v2.apartmentDetail.checklist.viewingContact",
        "Viewing contact"
      ),
      person: checklist.people.view,
    },
  ].filter(({ person }) => person.name.trim() || person.phone.trim());

  const openFile = async (path: string) => {
    if (!(await openStoredFile(path, CONTRACTS_BUCKET))) {
      toast.error(t("admin.docs.openFailed", "Could not open the document."));
    }
  };

  return (
    <Card className="p-5 space-y-4">
      <Header t={t} done={doneCount} total={15} />

      {/* 15-item status grid — straight from the stored rows. */}
      <div className="grid rounded-md border sm:grid-cols-2">
        {CHECKLIST_ITEM_LABELS.map(({ key, i18nKey, fallback }) => {
          const status = reviewStatus(byKey.get(key)?.status ?? "open");
          return (
            <div
              key={key}
              className="flex items-center gap-2 border-t px-3 py-1.5 text-xs first:border-t-0 sm:[&:nth-child(2)]:border-t-0"
            >
              {status === "done" ? (
                <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
              ) : status === "requested" ? (
                <Clock className="h-3.5 w-3.5 shrink-0 text-amber-600" />
              ) : (
                <CircleAlert className="h-3.5 w-3.5 shrink-0 text-destructive" />
              )}
              <span className="flex-1 truncate">{t(i18nKey, fallback)}</span>
              <span
                className={
                  status === "done"
                    ? "text-muted-foreground"
                    : status === "requested"
                      ? "text-amber-600"
                      : "text-destructive"
                }
              >
                {status === "done"
                  ? t("admin.wizard.step3.statusDone", "Done")
                  : status === "requested"
                    ? t("admin.wizard.step3.statusRequested", "Requested")
                    : t("admin.wizard.step3.statusMissing", "Missing")}
              </span>
            </div>
          );
        })}
      </div>

      {/* Contacts — un-folded from the checklist notes. */}
      {contacts.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t("admin.v2.apartmentDetail.checklist.contacts", "Contacts")}
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {contacts.map(({ label, person }) => (
              <div key={label} className="rounded-md border px-3 py-2 text-sm">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="font-medium">{person.name.trim() || "—"}</p>
                {person.phone.trim() && (
                  <p className="text-xs text-muted-foreground">
                    {person.phone.trim()}
                  </p>
                )}
                {person.email.trim() && (
                  <p className="truncate text-xs text-muted-foreground">
                    {person.email.trim()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Uploaded proof photos/files — private bucket, opened via signed URL. */}
      {files.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t(
              "admin.v2.apartmentDetail.checklist.photos",
              "Photos & proof files"
            )}
            <span className="ml-1.5 tabular-nums opacity-60">
              ({files.length})
            </span>
          </p>
          <ul className="divide-y rounded-md border">
            {files.map((f) => {
              const label = CHECKLIST_ITEM_LABELS.find(
                (l) => l.key === f.item_key
              );
              return (
                <li
                  key={f.id}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <Image className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {label ? t(label.i18nKey, label.fallback) : f.item_key}
                      </p>
                      {f.file_name && (
                        <p className="truncate text-xs text-muted-foreground">
                          {f.file_name}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => openFile(f.file_url)}
                  >
                    <FileText className="mr-1.5 h-3.5 w-3.5" />
                    {t("admin.action.open", "Open")}
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
}

function Header({
  t,
  done,
  total,
}: {
  t: (key: string, fallback?: string) => string;
  done: number;
  total: number;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">
          {t(
            "admin.v2.apartmentDetail.checklist.heading",
            "Onboarding checklist"
          )}
        </h3>
      </div>
      <Badge variant="outline" className="tabular-nums text-muted-foreground">
        {done}/{total}
      </Badge>
    </div>
  );
}
